import { AppWidgetState, DashboardPreference, DisplayNameType, TableSortType, VisualizationType } from '@ddv/models';

export class WidgetSettingsOverrides {
    customName?: string | undefined | null;
    displayNameType?: DisplayNameType;
    masterWidget: boolean;
    subscribedToDashboardFilters: boolean;
    filterToActiveDateNotRange?: boolean;
    defaultVisualizationType?: VisualizationType | null;
    defaultSlicerForDefaultVisualization?: string | undefined | null;
    defaultSortOnForDefaultVisualization?: TableSortType | undefined | null;
    defaultSortDirectionForDefaultVisualization?: 'ASC' | 'DESC' | 'CUSTOM' | undefined | null;
    realtimeUpdates: boolean;
    currentDashboardQueryParams?: DashboardPreference | undefined;

    constructor(init?: Partial<WidgetSettingsOverrides>) {
        this.customName = init?.customName;
        this.displayNameType = init?.displayNameType;
        this.masterWidget = !!init?.masterWidget;
        this.subscribedToDashboardFilters = !!init?.subscribedToDashboardFilters;
        this.filterToActiveDateNotRange = init?.filterToActiveDateNotRange;
        this.defaultVisualizationType = init?.defaultVisualizationType;
        this.defaultSlicerForDefaultVisualization = init?.defaultSlicerForDefaultVisualization;
        this.defaultSortOnForDefaultVisualization = init?.defaultSortOnForDefaultVisualization;
        this.defaultSortDirectionForDefaultVisualization = init?.defaultSortDirectionForDefaultVisualization;
        this.realtimeUpdates = !!init?.realtimeUpdates;
        this.currentDashboardQueryParams = init?.currentDashboardQueryParams;
    }

    clone(): WidgetSettingsOverrides {
        return new WidgetSettingsOverrides(this);
    }

    deriveEffectiveStateFrom(widgetSettings: AppWidgetState): WidgetSettingsOverrides {
        const effectiveState: WidgetSettingsOverrides = new WidgetSettingsOverrides();

        effectiveState.displayNameType = this.displayNameType ?? widgetSettings.displayNameType;
        effectiveState.customName = this.customName ?? widgetSettings.customDisplayName;
        effectiveState.masterWidget = !!(this.masterWidget || widgetSettings.isMaster);
        effectiveState.subscribedToDashboardFilters = !!(this.subscribedToDashboardFilters ??
            widgetSettings.isSubscribedToDashboardFilters);
        effectiveState.filterToActiveDateNotRange = this.filterToActiveDateNotRange ?? widgetSettings.widgetFilters?.activeDate != null;
        effectiveState.defaultVisualizationType = this.defaultVisualizationType ?? widgetSettings.getDefaultVisualizationType();
        effectiveState.defaultSlicerForDefaultVisualization = this.defaultSlicerForDefaultVisualization ??
            widgetSettings.getDefaultSlicerForVisualizationType(effectiveState.defaultVisualizationType);
        effectiveState.defaultSortOnForDefaultVisualization = this.defaultSortOnForDefaultVisualization ?? widgetSettings.sortTableBy;
        effectiveState.defaultSortDirectionForDefaultVisualization = this.defaultSortDirectionForDefaultVisualization ??
            widgetSettings.tableSortDirection;
        effectiveState.realtimeUpdates = !!(this.realtimeUpdates ?? widgetSettings.realtimeUpdates);

        return effectiveState;
    }

    applyToWidget(widgetSettings: AppWidgetState): void {
        widgetSettings.displayNameType = this.displayNameType ?? widgetSettings.displayNameType;
        widgetSettings.customDisplayName = this.customName ?? widgetSettings.customDisplayName;
        widgetSettings.isMaster = this.masterWidget;
        widgetSettings.isSubscribedToDashboardFilters = this.subscribedToDashboardFilters ?? widgetSettings.isSubscribedToDashboardFilters;
        widgetSettings.isActiveDate = this.filterToActiveDateNotRange ?? widgetSettings.isActiveDate;
        widgetSettings.setDefaultVisualizationType(this.defaultVisualizationType ?? widgetSettings.getDefaultVisualizationType());
        widgetSettings.sortTableBy = this.defaultSortOnForDefaultVisualization ?? widgetSettings.sortTableBy;
        widgetSettings.tableSortDirection = this.defaultSortDirectionForDefaultVisualization ?? widgetSettings.tableSortDirection;
        widgetSettings.realtimeUpdates = this.realtimeUpdates ?? widgetSettings.realtimeUpdates;
        widgetSettings.hideLoaderAfterFirstDataLoad = false;

        if (widgetSettings.isActiveDate && widgetSettings.widgetFilters) {
            widgetSettings.widgetFilters.activeDate = widgetSettings.widgetFilters.activeDate ?? widgetSettings.widgetFilters.endDate;
        } else if (widgetSettings.widgetFilters) {
            widgetSettings.widgetFilters.activeDate = null;
        }
    }

    applyDisplayNameType(value: DisplayNameType): void {
        this.displayNameType = value;

        if (value !== 'CUSTOM') {
            this.applyCustomName(null);
        }
    }

    applyCustomName(value: string | null): void {
        this.customName = value;
    }

    applyIsSubscribedToDashboardFilters(value: boolean): void {
        this.subscribedToDashboardFilters = value;
    }

    applyMasterWidget(value: boolean): void {
        this.masterWidget = value;

        if (!value) {
            this.subscribedToDashboardFilters = true;
        }
    }

    applyRealtimeUpdates(value: boolean): void {
        this.realtimeUpdates = value;
    }

    applyDefaultVisualizationType(value: VisualizationType | null): void {
        this.defaultVisualizationType = value;

        this.defaultSlicerForDefaultVisualization = null;
        this.defaultSortOnForDefaultVisualization = null;
        this.defaultSortDirectionForDefaultVisualization = null;
    }

    applyDefaultSlicerForDefaultVisualization(value: string | null): void {
        this.defaultSlicerForDefaultVisualization = value;
    }

    applyDefaultSortOnForDefaultVisualization(value: TableSortType | null): void {
        this.defaultSortOnForDefaultVisualization = value;
    }

    applyDefaultSortDirectionForDefaultVisualization(value: 'ASC' | 'DESC' | 'CUSTOM' | null): void {
        this.defaultSortDirectionForDefaultVisualization = value;
    }

    applyCurrentDashboardQueryParams(value: DashboardPreference | undefined): void {
        this.currentDashboardQueryParams = value;
    }

    enumerateChanges(original: WidgetSettingsOverrides): WidgetSettingsOverrides {
        const changes: WidgetSettingsOverrides = new WidgetSettingsOverrides();

        if (this.displayNameType !== original.displayNameType) {
            changes.displayNameType = this.displayNameType;
        }

        if (this.customName !== original.customName) {
            changes.customName = this.customName;
        }

        if (this.masterWidget !== original.masterWidget) {
            changes.masterWidget = this.masterWidget;
        }

        if (this.subscribedToDashboardFilters !== original.subscribedToDashboardFilters) {
            changes.subscribedToDashboardFilters = this.subscribedToDashboardFilters;
        }

        if (this.filterToActiveDateNotRange !== original.filterToActiveDateNotRange) {
            changes.filterToActiveDateNotRange = this.filterToActiveDateNotRange;
        }

        if (this.defaultVisualizationType !== original.defaultVisualizationType) {
            changes.defaultVisualizationType = this.defaultVisualizationType;
        }

        if (this.defaultSlicerForDefaultVisualization !== original.defaultSlicerForDefaultVisualization) {
            changes.defaultSlicerForDefaultVisualization = this.defaultSlicerForDefaultVisualization;
        }

        if (this.defaultSortOnForDefaultVisualization !== original.defaultSortOnForDefaultVisualization) {
            changes.defaultSortOnForDefaultVisualization = this.defaultSortOnForDefaultVisualization;
        }

        if (this.defaultSortDirectionForDefaultVisualization !== original.defaultSortDirectionForDefaultVisualization) {
            changes.defaultSortDirectionForDefaultVisualization = this.defaultSortDirectionForDefaultVisualization;
        }

        if (this.realtimeUpdates !== original.realtimeUpdates) {
            changes.realtimeUpdates = this.realtimeUpdates;
        }

        return changes;
    }
}
